import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { attendanceErrorMessage } from "@/lib/attendanceErrors";
import DailyCheckInPass from "@/components/DailyCheckInPass";
import { Button } from "@/components/ui/button";
import { QrCode } from "lucide-react";

function locationSecureContextOk() {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname || "";
  if (host === "localhost" || host === "127.0.0.1") return true;
  return Boolean(window.isSecureContext);
}

async function readBrowserLocation() {
  if (!navigator.geolocation) {
    return { location_status: "unavailable" };
  }
  if (!locationSecureContextOk()) {
    return { location_status: "permission_denied" };
  }
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      });
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy_m: pos.coords.accuracy,
      location_status: "ok",
    };
  } catch (err) {
    if (err && err.code === 1) return { location_status: "permission_denied" };
    return { location_status: "unavailable" };
  }
}

function Spinner({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
      <div
        className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

/**
 * Worker home screen: Daily Check-In Pass when checked in (or checked out today).
 */
export default function AttendancePassPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get("/attendance/my-pass");
      setData(res);
    } catch (err) {
      setError(attendanceErrorMessage(err, "Unable to load your pass."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    load();
  }, [authLoading, user, load]);

  async function handleCheckOut() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const location = await readBrowserLocation();
      const { data: res } = await api.post("/attendance/my-pass/check-out", location);
      setData(res);
    } catch (err) {
      setError(attendanceErrorMessage(err, "Check-out failed. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function handleAway() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data: res } = await api.post("/attendance/my-pass/away");
      setData(res);
    } catch (err) {
      setError(attendanceErrorMessage(err, "Could not start temporary absence."));
    } finally {
      setBusy(false);
    }
  }

  async function handleReturn() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data: res } = await api.post("/attendance/my-pass/return");
      setData(res);
      if (res.needs_resume) {
        setError(res.message || "Grace period ended. Tap Resume to continue.");
      }
    } catch (err) {
      setError(attendanceErrorMessage(err, "Could not record your return."));
    } finally {
      setBusy(false);
    }
  }

  async function handleResume() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data: res } = await api.post("/attendance/my-pass/resume");
      setData(res);
    } catch (err) {
      setError(attendanceErrorMessage(err, "Could not resume check-in."));
    } finally {
      setBusy(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Spinner label="Loading your pass…" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4 max-w-sm">
          <p className="text-muted-foreground">Scan your site QR to check in.</p>
          <Button asChild variant="outline">
            <Link to="/login">Sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  const pass = data?.pass;
  const showPass = Boolean(data?.has_pass && pass);

  return (
    <div
      className="min-h-screen bg-background flex flex-col px-4 py-6 safe-area-pb"
      data-testid="attendance-pass-page"
    >
      {showPass ? (
        <DailyCheckInPass
          pass={pass}
          showCheckOut={Boolean(data?.can_check_out)}
          onCheckOut={handleCheckOut}
          checkoutBusy={busy}
          canStartAway={Boolean(data?.can_start_away)}
          canReturn={Boolean(data?.can_return)}
          canResume={Boolean(data?.can_resume || data?.needs_resume)}
          onAway={handleAway}
          onReturn={handleReturn}
          onResume={handleResume}
          actionBusy={busy}
          footer={
            <div className="mt-4 space-y-2 text-center">
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              {pass.status === "checked_out" && (
                <p className="text-sm text-muted-foreground" data-testid="attendance-pass-closed-hint">
                  This session is closed. Scan the site QR to check in again.
                </p>
              )}
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  logout();
                }}
              >
                Sign out
              </button>
            </div>
          }
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 max-w-md mx-auto text-center">
          <div className="flex items-center gap-2 text-primary">
            <QrCode size={22} aria-hidden="true" />
            <span className="font-heading font-semibold text-sm tracking-wide">Smart Attendance</span>
          </div>
          <h1 className="font-heading text-2xl font-bold">No active pass</h1>
          <p className="text-muted-foreground">
            Scan the site QR code on the job site to check in. Your Daily Check-In Pass will open
            here automatically.
          </p>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <Button variant="outline" onClick={load}>
            Refresh
          </Button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => logout()}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
